import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Clock, ArrowRight, Target, Lock, RotateCcw } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { LanguageToggle } from "@/components/LanguageToggle";
import { DEMO_SONGS, getSongById } from "@/data/demoSongs";
import { Song, JamTake } from "@/models/types";
import { useI18n } from "@/i18n/I18nContext";
import { useApp } from "@/context/AppContext";
import { JamTakeService } from "@/services/JamTakeService";

const fmt = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

// Deterministic waveform bar heights per song id (no external assets).
function waveHeights(seed: string, n = 44): number[] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    h = (h * 1103515245 + 12345) >>> 0;
    const base = 0.25 + ((h >>> 8) % 1000) / 1000 * 0.75;
    const shape = Math.sin((i / n) * Math.PI); // fuller in the middle
    out.push(Math.max(0.15, base * (0.5 + shape * 0.6)));
  }
  return out;
}

const Waveform: React.FC<{ seed: string; className?: string }> = ({ seed, className }) => {
  const heights = React.useMemo(() => waveHeights(seed), [seed]);
  return (
    <div className={`flex h-full w-full items-center gap-[3px] ${className || ""}`}>
      {heights.map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-full"
          style={{
            height: `${v * 72}%`,
            background: "linear-gradient(180deg, rgba(245,158,11,0.55), rgba(217,119,6,0.15))",
            opacity: 0.35 + v * 0.4,
          }}
        />
      ))}
    </div>
  );
};

// Training jams reuse existing prepared song timelines (mock data for Build 1).
const TRAINING = [
  { songId: "midnight-groove" },
  { songId: "amber-drift" },
  { songId: "velvet-minor" },
];

export default function SongLibrary() {
  const navigate = useNavigate();
  const { t, tm, dict } = useI18n();
  const { setResumeTake } = useApp();
  const takes = JamTakeService.list();

  const resume = (take: JamTake) => {
    setResumeTake(take);
    navigate(`/jam/${take.songId}`);
  };

  return (
    <AppShell active="library">
      <div className="mb-4 flex justify-end">
        <LanguageToggle testId="library-language-toggle" />
      </div>
      {/* Header */}
      <div className="mb-1">
        <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-amber-400/90">
          {t("library.kicker")}
        </span>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-100 sm:text-4xl">
          {t("library.header")}
        </h1>
        <p className="mt-2 max-w-xl text-sm text-slate-400">{t("library.sub")}</p>
      </div>

      {/* Section 1 — Recommended */}
      <SectionTitle>{t("library.recommended")}</SectionTitle>
      <div className="grid gap-5 lg:grid-cols-2">
        {DEMO_SONGS.map((song, i) => (
          <SongCard
            key={song.id}
            song={song}
            index={i}
            onJam={() => navigate(`/jam/${song.id}`)}
            onPrepare={() => navigate(`/prepare/${song.id}`)}
          />
        ))}
      </div>

      {/* Section 2 — Continue */}
      <SectionTitle>{t("library.continueTitle")}</SectionTitle>
      {takes.length === 0 ? (
        <div
          data-testid="library-empty-takes"
          className="rounded-2xl p-5 text-sm text-slate-400"
          style={{ background: "rgba(18,21,25,0.4)", border: "1px dashed rgba(255,255,255,0.09)" }}
        >
          {t("library.emptyTakes")}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {takes.slice(0, 4).map((take) => (
            <div
              key={take.id}
              data-testid={`library-take-${take.id}`}
              className="flex items-center justify-between gap-3 rounded-2xl p-4"
              style={{ background: "rgba(18,21,25,0.8)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-slate-100">{take.songTitle}</div>
                <div className="truncate text-xs text-slate-400">{take.versionName}</div>
                <div className="mt-1.5">
                  <Chip>{tm("progress", take.progress)}</Chip>
                </div>
              </div>
              <button
                data-testid={`library-keep-playing-${take.id}`}
                onClick={() => resume(take)}
                className="flex shrink-0 items-center gap-2 rounded-full px-4 py-2.5 text-[11px] font-semibold text-[#1a1204] transition active:scale-95"
                style={{ background: "linear-gradient(135deg,#FDE68A,#F59E0B 60%,#D97706)" }}
              >
                <RotateCcw size={14} />
                {t("library.keepPlaying")}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Section 3 — Training */}
      <SectionTitle>{t("library.trainingTitle")}</SectionTitle>
      <div className="grid gap-3 sm:grid-cols-3">
        {dict.library.training.map((card, i) => {
          const target = TRAINING[i]?.songId ?? DEMO_SONGS[0].id;
          return (
            <button
              key={card.t}
              data-testid={`training-jam-${i}`}
              onClick={() => navigate(`/jam/${target}`)}
              className="group flex flex-col gap-2 rounded-2xl p-4 text-left transition active:scale-[0.99]"
              style={{ background: "rgba(18,21,25,0.6)", border: "1px solid rgba(255,255,255,0.07)" }}
            >
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full"
                style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.25)" }}
              >
                <Target size={15} className="text-amber-300" />
              </span>
              <div className="mt-1 flex items-center gap-1 text-sm font-medium text-slate-100">
                {card.t}
                <ArrowRight size={14} className="text-amber-400/70 transition group-hover:translate-x-0.5" />
              </div>
              <div className="text-xs leading-relaxed text-slate-400">{card.s}</div>
            </button>
          );
        })}
      </div>

      {/* Custom song placeholder — future */}
      <div className="mt-10">
        <div
          data-testid="custom-song-placeholder"
          className="flex flex-col items-start gap-3 rounded-2xl p-5 sm:flex-row sm:items-center sm:justify-between"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.1)" }}
        >
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full" style={{ background: "rgba(255,255,255,0.04)" }}>
              <Lock size={16} className="text-slate-500" />
            </span>
            <div>
              <div className="text-sm font-medium text-slate-300">{t("library.customTitle")}</div>
              <div className="mt-1 max-w-md text-xs text-slate-500">{t("library.customText")}</div>
            </div>
          </div>
          <button
            data-testid="custom-song-coming-soon-btn"
            disabled
            className="cursor-not-allowed rounded-full px-4 py-2.5 text-[11px] font-mono uppercase tracking-widest text-slate-500"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            {t("library.comingSoon")}
          </button>
        </div>
      </div>
    </AppShell>
  );
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="mb-4 mt-10 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-400">
    {children}
  </h2>
);

const SongCard: React.FC<{
  song: Song;
  index: number;
  onJam: () => void;
  onPrepare: () => void;
}> = ({ song, index, onJam, onPrepare }) => {
  const { t, tm, dict } = useI18n();
  const focus = dict.songs[song.id]?.focus;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      data-testid={`song-card-${song.id}`}
      className="overflow-hidden rounded-3xl"
      style={{ background: "rgba(18,21,25,0.85)", border: "1px solid rgba(255,255,255,0.08)" }}
    >
      {/* waveform texture */}
      <div className="relative h-20 px-6 pt-5">
        <Waveform seed={song.id} />
        <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(18,21,25,0) 8%,rgba(18,21,25,0.9) 62%,rgba(18,21,25,1) 92%)" }} />
      </div>

      <div className="px-6 pb-6 pt-1">
        <h3 className="text-2xl font-semibold tracking-tight text-slate-100">{song.title}</h3>
        <div className="mt-1 text-sm text-amber-200/80">{tm("styles", song.style)}</div>

        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-xs text-slate-400">
          <span>{tm("keys", song.key)}</span>
          <span className="text-slate-600">·</span>
          <span>{song.bpm} {t("common.bpm")}</span>
          <span className="text-slate-600">·</span>
          <span>{song.timeSignature[0]}/{song.timeSignature[1]}</span>
          <span className="text-slate-600">·</span>
          <span className="inline-flex items-center gap-1"><Clock size={12} />{fmt(song.duration)}</span>
          <Chip>{tm("difficulty", song.difficulty)}</Chip>
        </div>

        {focus && (
          <div className="mt-3 flex items-center gap-2 text-xs text-slate-300">
            <Target size={13} className="text-amber-400/80" />
            <span>
              <span className="text-slate-500">{t("library.focusLabel")}: </span>
              {focus}
            </span>
          </div>
        )}

        <div className="mt-5 flex items-center gap-3">
          <button
            data-testid={`jam-start-${song.id}`}
            onClick={onJam}
            className="flex flex-1 items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold text-[#1a1204] transition active:scale-95"
            style={{ background: "linear-gradient(135deg,#FDE68A,#F59E0B 60%,#D97706)", boxShadow: "0 0 24px rgba(245,158,11,0.35)" }}
          >
            <Play size={16} fill="#1a1204" />
            {t("common.startJam")}
          </button>
          <button
            data-testid={`prepare-${song.id}`}
            onClick={onPrepare}
            className="rounded-full px-5 py-3 text-xs font-mono uppercase tracking-widest text-slate-300 transition hover:text-amber-200 active:scale-95"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)" }}
          >
            {t("library.prepare")}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    className="rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-amber-200"
    style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)" }}
  >
    {children}
  </span>
);
